import React, { useEffect, useState } from "react";
import { api, clearSession } from "./api";

export interface AuthUser { id: string; username: string; role: "super_admin" | "user"; mustChangePassword: boolean }

export function AuthGate({ children }: { children: (user: AuthUser, logout: () => Promise<void>) => React.ReactNode }) {
  const [mode, setMode] = useState<"loading" | "install" | "login" | "ready">("loading");
  const [username, setUsername] = useState("");
  const [account, setAccount] = useState<AuthUser | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("hotcoin.post.theme") || "light");
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("hotcoin.post.theme", theme);
  }, [theme]);
  useEffect(() => {
    const reset = () => { setPassword(""); setMode("login"); };
    window.addEventListener("hotcoin:unauthorized", reset);
    void (async () => {
      try {
        const { installed } = await api<{ installed: boolean }>("/api/install/status");
        if (!installed) return setMode("install");
        const user = await api<AuthUser>("/api/auth/me");
        setAccount(user);
        setUsername(user.username);
        setMode("ready");
      } catch (reason) {
        setMode("login");
        if (!(reason instanceof Error) || reason.message !== "请先登录") setError(String(reason instanceof Error ? reason.message : reason));
      }
    })();
    return () => window.removeEventListener("hotcoin:unauthorized", reset);
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    if (mode === "install" && password !== confirmation) return setError("两次输入的密码不一致");
    setWorking(true);
    try {
      if (mode === "install") {
        await api("/api/install", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ admin: { username, password } }) });
        setPassword("");
        setConfirmation("");
        setMode("login");
      } else {
        const user = await api<AuthUser>("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
        setAccount(user);
        clearSession();
        setUsername(user.username);
        setPassword("");
        setMode("ready");
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      if (mode === "login") setPassword("");
    } finally { setWorking(false); }
  }
  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    clearSession();
    setError("");
    setMode("login");
    setTheme(localStorage.getItem("hotcoin.post.theme") || "light");
  }
  if (mode === "ready" && account) {
    if (account.mustChangePassword) return <main className="auth-layout"><div className="first-password"><p>首次登录, 请先修改初始密码</p><PasswordSettings onChanged={async () => setAccount(await api<AuthUser>("/api/auth/me"))}/><button className="btn" onClick={() => void logout()}>退出登录</button></div></main>;
    return children(account, logout);
  }
  return <main className="auth-layout">
    <button className="btn btn-ghost auth-theme-toggle" aria-label="切换主题" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>{theme === "light" ? "☾" : "☀"}</button>
    <section className="card auth-card">
      <div className="auth-brand"><img src={`/hotcoin-assets/console-logo-${theme}.png`} alt="Hotcoin"/></div>
      <p className="auth-subtitle">{mode === "loading" ? "正在加载" : mode === "install" ? "创建管理员账号" : "账户登录"}</p>
      {mode !== "loading" && <form onSubmit={(event) => void submit(event)}>
        <label className="field"><span>用户名</span><input autoComplete="username" autoFocus required value={username} onChange={(event) => setUsername(event.target.value)}/></label>
        <label className="field"><span>密码</span><input type="password" autoComplete={mode === "install" ? "new-password" : "current-password"} required minLength={mode === "install" ? 8 : 1} value={password} onChange={(event) => setPassword(event.target.value)}/></label>
        {mode === "install" && <label className="field"><span>确认密码</span><input type="password" autoComplete="new-password" required minLength={8} value={confirmation} onChange={(event) => setConfirmation(event.target.value)}/></label>}
        <p className="form-error" role="alert">{error}</p>
        <button className="btn btn-primary" disabled={working}>{working ? "请稍候" : mode === "install" ? "完成初始化" : "登录"}</button>
      </form>}
    </section>
  </main>;
}

export function PasswordSettings({ onChanged }: { onChanged?: () => Promise<void> } = {}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    setMessage("");
    if (newPassword !== confirmation) return setError("两次输入的密码不一致");
    setWorking(true);
    try {
      await api("/api/auth/password", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ currentPassword, newPassword }) });
      setCurrentPassword(""); setNewPassword(""); setConfirmation("");
      setMessage("密码已修改");
      await onChanged?.();
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setWorking(false); }
  }
  return <section className="card"><div className="card-header"><h2>修改登录密码</h2></div><form className="form-grid" onSubmit={(event) => void submit(event)}>
    <label className="field"><span>当前密码</span><input type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)}/></label>
    <label className="field"><span>新密码</span><input type="password" autoComplete="new-password" required minLength={8} value={newPassword} onChange={(event) => setNewPassword(event.target.value)}/></label>
    <label className="field"><span>确认新密码</span><input type="password" autoComplete="new-password" required minLength={8} value={confirmation} onChange={(event) => setConfirmation(event.target.value)}/></label>
    <div className="full password-actions"><span className={error ? "error-text" : "hint"} role="status">{error || message}</span><button className="btn" disabled={working}>修改密码</button></div>
  </form></section>;
}
